// ProjectsPage — the author's projects (pen "Projects and Studio" 00–02,
// decision 3, decided 2026-10-20), pure.
//
// One column: the title, a one-line lede while there are few projects, then
// the Sort button and the view toggle (Compact list / Detailed cards) on the
// right. Pinned projects first (PINNED, in pin order), then the others
// (OTHERS, by the chosen sort). A card or row opens its project; its "…"
// holds the same menu as the left menu's rows, without "Remove from Recent".
// Names only, no colour marker. No projects: an empty state that says how
// one comes about.
//
// Props-driven: the items (with their actions) and both views come in; every
// choice reports up as a `RecentListCommand`. Pins are the shared "projects"
// list's (`pins`); sort and density are this page's own (`page`).

export { ProjectsPage };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import {
    RECENT_SORTS,
    type RecentDensity,
    type RecentListCommand,
    type RecentListView,
    recentSectionsOf,
} from "../Sidebar/recentList.js";
import { useRowMenu, useSortLabel } from "../Sidebar/SidebarRecent.js";
import type { RecentItem } from "../Sidebar/sidebarEntries.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { TextTabs } from "../TextTabs/TextTabs.js";
import { ViewColumn, ViewHeader } from "../ViewHeader/ViewHeader.js";
import "./Projects.css";

type ProjectsPageProps = {
    readonly items: readonly RecentItem[];
    // The archived projects (the Archived tab: Restore, Delete …).
    readonly archived: readonly RecentItem[];
    // How many cards lie on a project's canvas (its miniature), by id.
    readonly cardsOf: (id: string) => number;
    // Pins (shared with the left menu).
    readonly pins: RecentListView;
    readonly onPinsCommand: (cmd: RecentListCommand) => void;
    // This page's sort and density.
    readonly page: RecentListView;
    readonly onPageCommand: (cmd: RecentListCommand) => void;
    // The projects are still being read: skeletons in the real shape.
    readonly loading: boolean;
    // Set up a new project (the empty state's way forward).
    readonly onNewProject: () => void;
};

// The lede explains what a project is until there are more than this many.
const LEDE_MAX = 3;
const SKELETONS = 3;
// The miniature: at most this many card shapes.
const MINI_MAX = 4;

const densityOf = (value: string): RecentDensity | undefined =>
    value === "compact" || value === "detailed" ? value : undefined;

// A miniature of the project: one small card per card on its canvas (at
// most four); an empty project shows its dot grid and "Empty".
const Thumbnail = ({ cards }: { readonly cards: number }): ReactNode => {
    const { t } = useLingui();
    return (
        <div
            className="bilda-projects__thumb"
            data-empty={cards === 0 ? "true" : undefined}
            aria-hidden="true"
        >
            {cards === 0 ? (
                <span className="bilda-projects__thumb-empty">{t`Empty`}</span>
            ) : (
                Array.from({ length: Math.min(cards, MINI_MAX) }, (_, i) => (
                    <span
                        // biome-ignore lint/suspicious/noArrayIndexKey: fixed shapes
                        key={i}
                        className="bilda-projects__mini"
                        data-at={i}
                    />
                ))
            )}
            {/* Shown on hover (pen 00, card states). */}
            <span className="bilda-projects__open-chip">{t`Open`}</span>
        </div>
    );
};

const Project = ({
    item,
    cards,
    pinned,
    detailed,
    menu,
}: {
    readonly item: RecentItem;
    readonly cards: number;
    readonly pinned: boolean;
    readonly detailed: boolean;
    readonly menu: (item: RecentItem, pinned: boolean) => readonly MenuItem[];
}): ReactNode => {
    const { t } = useLingui();
    const name = (
        <span className="bilda-projects__name" title={item.title}>
            {item.title}
        </span>
    );
    const meta = <span className="bilda-projects__meta">{item.meta}</span>;
    // While renaming, the field owns the row: it takes the name's place
    // outside the opening button, and the "…" leaves, so the menu that
    // started the rename can't take the focus back from the field.
    if (item.renaming !== undefined) {
        const field = (
            <RowRename
                initial={item.title}
                label={t`New name for ${item.title}`}
                onSubmit={item.renaming.onSubmit}
                onCancel={item.renaming.onCancel}
                className="bilda-projects__rename"
            />
        );
        return detailed ? (
            <li className="bilda-projects__card">
                <Thumbnail cards={cards} />
                {field}
                {meta}
            </li>
        ) : (
            <li className="bilda-projects__row bilda-projects__row--renaming">
                {field}
                {meta}
            </li>
        );
    }
    const more = (
        <RowOverflow
            label={t`More options for ${item.title}`}
            items={menu(item, pinned)}
            className="bilda-projects__more"
        />
    );
    // The whole card or row opens the project; its "…" sits beside the name.
    return detailed ? (
        <li
            className="bilda-projects__card bilda-row-overflow-host"
            data-archived={item.restore === undefined ? undefined : "true"}
        >
            <button
                type="button"
                className="bilda-projects__open"
                onClick={item.open}
            >
                <Thumbnail cards={cards} />
                {name}
                {meta}
            </button>
            {more}
        </li>
    ) : (
        <li className="bilda-projects__row bilda-row-overflow-host">
            <button
                type="button"
                className="bilda-projects__open bilda-projects__row-open"
                onClick={item.open}
            >
                {name}
                {meta}
            </button>
            {more}
        </li>
    );
};

type ProjectsTab = "active" | "archived";

const ProjectsPage = ({
    items,
    archived,
    pins,
    onPinsCommand,
    page,
    onPageCommand,
    cardsOf,
    loading,
    onNewProject,
}: ProjectsPageProps): ReactNode => {
    const { t, i18n } = useLingui();
    const sortLabel = useSortLabel();
    const menu = useRowMenu(onPinsCommand, "page");
    const detailed = page.density === "detailed";
    // Which tab is shown (view state: a reload starts on Active).
    const [tab, setTab] = useState<ProjectsTab>("active");
    const showing: ProjectsTab = archived.length === 0 ? "active" : tab;
    const sections = recentSectionsOf(
        items,
        { ...page, pinned: pins.pinned, hidden: [], opened: pins.opened },
        i18n.locale,
        Number.POSITIVE_INFINITY,
    );

    const controls = (
        <div className="bilda-projects__controls">
            <Menu
                align="end"
                trigger={
                    <Button
                        variant="secondary"
                        size="sm"
                        className="bilda-projects__sort"
                        aria-label={t`Sort: ${sortLabel(page.sort)}`}
                    >
                        {sortLabel(page.sort)}
                        <Icon name="chevron-down" />
                    </Button>
                }
                items={[
                    { _tag: "label", label: t`Sort by` },
                    ...RECENT_SORTS.map(
                        (sort): MenuItem => ({
                            _tag: "item",
                            label: sortLabel(sort),
                            checked: page.sort === sort,
                            onSelect: () =>
                                onPageCommand({ type: "sort-by", sort }),
                        }),
                    ),
                ]}
            />
            <SegmentedControl
                size="sm"
                ariaLabel={t`View`}
                value={page.density}
                onChange={(value) => {
                    const density = densityOf(value);
                    if (density !== undefined)
                        onPageCommand({ type: "show-density", density });
                }}
                options={[
                    {
                        value: "compact",
                        label: t`Compact`,
                        icon: <Icon name="list" />,
                    },
                    {
                        value: "detailed",
                        label: t`Detailed`,
                        icon: <Icon name="layout-grid" />,
                    },
                ]}
            />
        </div>
    );

    const list = (
        label: string,
        rows: readonly RecentItem[],
        pinned: boolean,
    ): ReactNode =>
        rows.length === 0 ? null : (
            <section className="bilda-projects__section" aria-label={label}>
                {sections.pinned.length > 0 ? (
                    <h2 className="bilda-projects__label">{label}</h2>
                ) : null}
                <ul
                    className={
                        detailed
                            ? "bilda-projects__grid"
                            : "bilda-projects__list"
                    }
                >
                    {rows.map((item) => (
                        <Project
                            key={item.id}
                            item={item}
                            cards={cardsOf(item.id)}
                            pinned={pinned}
                            detailed={detailed}
                            menu={menu}
                        />
                    ))}
                </ul>
            </section>
        );

    const body = (): ReactNode => {
        if (loading)
            return (
                <ul
                    className={
                        detailed
                            ? "bilda-projects__grid"
                            : "bilda-projects__list"
                    }
                    aria-busy="true"
                >
                    {Array.from({ length: SKELETONS }, (_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholders
                        <li key={i} className="bilda-projects__skeleton">
                            <Skeleton
                                width="100%"
                                height={detailed ? "12.5rem" : "1rem"}
                            />
                        </li>
                    ))}
                </ul>
            );
        if (items.length === 0 && showing === "active")
            return (
                <EmptyState
                    title={t`No projects yet`}
                    body={t`A project is a free surface for sources and ideas.`}
                >
                    <Button variant="primary" onClick={onNewProject}>
                        {t`New project`}
                    </Button>
                </EmptyState>
            );
        if (showing === "archived")
            // No pins and no sort here: archived ones in the Library's order.
            return list(t`Archived`, archived, false);
        return (
            <>
                {list(t`Pinned`, sections.pinned, true)}
                {list(t`Others`, sections.recent, false)}
            </>
        );
    };

    // Active n · Archived n (the Archived tab once something is archived).
    const tabs =
        archived.length === 0 ? null : (
            <TextTabs<ProjectsTab>
                ariaLabel={t`Show projects`}
                value={showing}
                onChange={setTab}
                tabs={[
                    { value: "active", label: t`Active ${items.length}` },
                    {
                        value: "archived",
                        label: t`Archived ${archived.length}`,
                    },
                ]}
            />
        );

    return (
        <div className="bilda-projects">
            <ViewColumn>
                <ViewHeader
                    title={t`Projects`}
                    tools={
                        items.length <= LEDE_MAX ? (
                            <p className="bilda-projects__lede">
                                {t`A project is a free surface where you put sources and ideas loosely and put them together into new material.`}
                            </p>
                        ) : null
                    }
                    nav={
                        items.length === 0 && archived.length === 0 ? null : (
                            <div className="bilda-projects__nav">
                                {tabs}
                                {showing === "active" && items.length > 0
                                    ? controls
                                    : null}
                            </div>
                        )
                    }
                />
                {body()}
            </ViewColumn>
        </div>
    );
};
